/**
 * Wind Particle System Configuration
 * Central config for data sources, color ramps, and GPU-adaptive settings
 */

import type { GPUTier, GeoBounds, WindDataSource, WindParticleConfig } from '@/types/wind';

/**
 * HRRR coverage bounding box (Continental US + nearby waters)
 */
const HRRR_BOUNDS: GeoBounds = {
  west: -134.1,
  south: 21.1,
  east: -60.9,
  north: 52.6,
};

/**
 * Wind data base URL
 * Local dev: run `python run_local.py` in seawatch-wind-pipeline, then use localhost.
 * Production: CloudFront distribution.
 */
const WIND_BASE_URL = import.meta.env.VITE_WIND_BASE_URL || 'https://d2bnidopdehcn0.cloudfront.net';

/**
 * Wind data sources — ordered by priority (highest resolution first)
 * The loader picks the best source for the current viewport and zoom.
 */
export const WIND_DATA_SOURCES: WindDataSource[] = [
  {
    name: 'HRRR 3km',
    tileUrl: `${WIND_BASE_URL}/hrrr/latest/tiles`,
    metadataUrl: `${WIND_BASE_URL}/hrrr/latest/metadata.json`,
    minZoom: 0,
    maxZoom: 9,
    bounds: HRRR_BOUNDS,
  },
  {
    name: 'GFS Global',
    tileUrl: `${WIND_BASE_URL}/gfs/latest/tiles`,
    metadataUrl: `${WIND_BASE_URL}/gfs/latest/metadata.json`,
    minZoom: 0,
    maxZoom: 5,
    bounds: null, // global
  },
];

/**
 * Wind particle color ramp — maps wind speed (m/s) to RGBA
 * Each entry: [speed, r, g, b, a]
 * High-luminosity tactical palette — visible on any terrain/ocean
 * Every stop is bright and saturated for maximum contrast
 */
export const WIND_COLOR_RAMP: [number, number, number, number, number][] = [
  [0.0, 58, 72, 127, 255], // calm: deep navy
  [2.0, 68, 115, 169, 255], // light: steel blue
  [4.0, 59, 152, 185, 255], // light breeze: teal
  [6.0, 57, 181, 165, 255], // gentle: cyan-green
  [8.0, 80, 190, 105, 255], // moderate: green
  [10.0, 145, 200, 66, 255], // fresh: lime-green
  [13.0, 210, 195, 48, 255], // strong: yellow
  [16.0, 230, 150, 35, 255], // near gale: amber
  [19.0, 230, 95, 30, 255], // gale: orange
  [23.0, 215, 44, 38, 255], // strong gale: red
  [28.0, 180, 30, 85, 255], // storm: crimson
  [33.0, 140, 40, 130, 255], // violent storm: purple
  [40.0, 220, 220, 240, 255], // hurricane: white-lavender
];

/**
 * GPU-adaptive particle settings
 */
export const PARTICLE_CONFIG: Record<GPUTier, WindParticleConfig> = {
  high: {
    particleCount: 50000,
    maxAge: 100,
    speedFactor: 0.4,
    fadeOpacity: 0.993,
    pointSize: 2.5,
  },
  medium: {
    particleCount: 25000,
    maxAge: 90,
    speedFactor: 0.35,
    fadeOpacity: 0.990,
    pointSize: 2.0,
  },
  low: {
    particleCount: 8000,
    maxAge: 70,
    speedFactor: 0.3,
    fadeOpacity: 0.985,
    pointSize: 1.5,
  },
};

/**
 * Calculate adaptive particle count based on GPU tier, zoom level, and screen size.
 * Globe zoom uses fewer particles (most are off-screen), mid-zoom gets full count,
 * high zoom tapers since data resolution limits usefulness.
 */
export function getAdaptiveParticleCount(
  baseTier: GPUTier,
  zoom: number,
  screenWidth: number,
  screenHeight: number,
): number {
  const base = PARTICLE_CONFIG[baseTier].particleCount;

  // Screen area factor: normalize to 1080p baseline
  const screenFactor = Math.sqrt((screenWidth * screenHeight) / (1920 * 1080));

  // Zoom factor: scale particle count by what's useful at each zoom range
  let zoomFactor: number;
  if (zoom < 2) {
    zoomFactor = 0.4; // globe: 40% — most particles off-screen
  } else if (zoom < 4) {
    zoomFactor = 0.4 + ((zoom - 2) / 2) * 0.6; // ramp to 100%
  } else if (zoom < 8) {
    zoomFactor = 1.0; // sweet spot: full count
  } else {
    zoomFactor = Math.max(0.5, 1.0 - (zoom - 8) * 0.1); // high zoom: taper
  }

  // Round to nearest 1000 for clean buffer sizes
  return Math.max(2000, Math.round((base * screenFactor * zoomFactor) / 1000) * 1000);
}

/**
 * Detect GPU tier from WebGL renderer string
 */
export function detectGPUTier(gl: WebGLRenderingContext | WebGL2RenderingContext): GPUTier {
  const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
  if (!debugInfo) return 'medium';

  const renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) as string;
  const rendererLower = renderer.toLowerCase();

  // High-end: dedicated GPUs and Apple Silicon
  if (
    rendererLower.includes('nvidia') ||
    rendererLower.includes('radeon') ||
    rendererLower.includes('apple m') ||
    rendererLower.includes('apple gpu')
  ) {
    return 'high';
  }

  // Medium: modern integrated GPUs
  if (rendererLower.includes('intel iris') || rendererLower.includes('intel uhd') || rendererLower.includes('adreno')) {
    return 'medium';
  }

  // Low: everything else
  return 'low';
}

/**
 * Generate a 256x1 color ramp texture data from the color stops
 * Used to map wind speed → color on the GPU via texture lookup
 */
export function generateColorRampData(): Uint8Array {
  const data = new Uint8Array(256 * 4); // 256 pixels, RGBA

  for (let i = 0; i < 256; i++) {
    // Map pixel index (0-255) to wind speed (0 - maxSpeed)
    const maxSpeed = WIND_COLOR_RAMP[WIND_COLOR_RAMP.length - 1][0];
    const speed = (i / 255) * maxSpeed;

    // Find the two color stops this speed falls between
    let lower = WIND_COLOR_RAMP[0];
    let upper = WIND_COLOR_RAMP[WIND_COLOR_RAMP.length - 1];

    for (let j = 0; j < WIND_COLOR_RAMP.length - 1; j++) {
      if (speed >= WIND_COLOR_RAMP[j][0] && speed < WIND_COLOR_RAMP[j + 1][0]) {
        lower = WIND_COLOR_RAMP[j];
        upper = WIND_COLOR_RAMP[j + 1];
        break;
      }
    }

    // Linearly interpolate between the two stops
    const range = upper[0] - lower[0];
    const t = range === 0 ? 0 : (speed - lower[0]) / range;

    data[i * 4 + 0] = Math.round(lower[1] + t * (upper[1] - lower[1])); // R
    data[i * 4 + 1] = Math.round(lower[2] + t * (upper[2] - lower[2])); // G
    data[i * 4 + 2] = Math.round(lower[3] + t * (upper[3] - lower[3])); // B
    data[i * 4 + 3] = Math.round(lower[4] + t * (upper[4] - lower[4])); // A
  }

  return data;
}
